import { useAuthStore } from "../auth/store";

export interface Problem {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  code?: string;
  errors?: unknown;
  /** 409 integrity: the column whose unique value is already taken. */
  field?: string;
  /** 409 integrity (BUG-007): every field of the taken key and the record that already holds it. */
  fields?: string[];
  existing?: { entity: string; id: string; path: string } | null;
}

export class ApiError extends Error {
  constructor(public problem: Problem) {
    super(problem.detail ?? problem.title);
  }
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

export const API_BASE = import.meta.env.VITE_API_URL ?? "/api/v1";

export function qs(params?: Record<string, unknown>): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, typeof value === "string" ? value : JSON.stringify(value));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

/** One refresh at a time: parallel 401s (dashboard polling) share the same promise instead of racing (WMS-07). */
let refreshing: Promise<boolean> | null = null;
export function refreshOnce(): Promise<boolean> {
  if (!refreshing) {
    refreshing = useAuthStore
      .getState()
      .refresh()
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

/** Unix seconds when a JWT expires, or null when it cannot be read. */
export function tokenExpiresAt(token: string | null | undefined): number | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/** UX3010-13 (ADR 0018): the working organization goes with every request; the server checks the access. */
export const COMPANY_HEADER = "X-Company-Id";

/** Codes of the answers that mean «the saved working organization is gone or closed for this user». */
const COMPANY_REFUSALS = new Set(["company_unknown", "company_forbidden"]);

function setCompanyHeader(headers: Headers): boolean {
  const company = useAuthStore.getState().company;
  if (!company) return false;
  headers.set(COMPANY_HEADER, company.id);
  return true;
}

/**
 * The organization saved in this browser was closed or removed (403 / 422 «company_…»): forget it, ask the profile for
 * the working organization again and repeat the request once without the header — the user is never locked out.
 */
async function companyRefused(response: Response, sent: boolean): Promise<boolean> {
  if (!sent || (response.status !== 403 && response.status !== 422)) return false;
  const data = (await response.clone().json().catch(() => null)) as Partial<Problem> | null;
  if (!data?.code || !COMPANY_REFUSALS.has(data.code)) return false;
  useAuthStore.getState().setCompany(null);
  void useAuthStore.getState().loadMe().catch(() => undefined);
  return true;
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const token = useAuthStore.getState().accessToken;
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // D10: the server words times (dock conflicts) in the zone this screen shows them in
  const zone = deviceTimeZone();
  if (zone) headers.set("X-Time-Zone", zone);
  const companySent = setCompanyHeader(headers);
  let response: Response;
  try {
    response = await fetch(API_BASE + path, { ...init, headers });
  } catch {
    throw new ApiError({ title: "Нет связи с сервером", status: 0, detail: "Нет связи с сервером: проверьте сеть и повторите", code: "network" });
  }
  if (retry && (await companyRefused(response, companySent))) return request<T>(path, init, false);
  if (response.status === 401 && retry) {
    let refreshed = false;
    try {
      refreshed = await refreshOnce();
    } catch (e) {
      // the API is down or unreachable: keep the session, let the caller show the problem
      throw new ApiError({ title: "Сервер недоступен", status: 503, detail: e instanceof Error ? e.message : "Сервер недоступен", code: "unavailable" });
    }
    if (refreshed) return request<T>(path, init, false);
    useAuthStore.getState().logout("Сеанс завершён: войдите снова, и вы вернётесь на ту же страницу");
  }
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  if (!response.ok) throw new ApiError(problemOf(response, data));
  return data as T;
}

/**
 * The API answers with problem+json in Russian. A bare `{"detail": "Not Found"}` comes from the router itself: the
 * address is not on the server, which in practice means a tab opened before a release calls what is no longer there.
 */
export function problemOf(response: Pick<Response, "status" | "statusText">, data: Partial<Problem> | null): Problem {
  const problem: Problem = { title: response.statusText, status: response.status, ...(data ?? {}) };
  if (!data?.title && (response.status === 404 || response.status === 405) && /^(Not Found|Method Not Allowed)$/.test(String(data?.detail ?? response.statusText))) {
    return { ...problem, title: "Запрос не найден на сервере", detail: STALE_TAB, code: "stale_client" };
  }
  return problem;
}

export const STALE_TAB = "Сервер не знает такого запроса — похоже, эта вкладка открыта со старой версией кабинета. Обновите страницу (Ctrl+R или ⌘R) и повторите.";

/** A binary answer (a PDF of labels): the same auth and the same one-refresh rule as `request`. */
async function requestBlob(path: string, body: unknown, retry = true): Promise<{ blob: Blob; filename: string | null }> {
  const headers = new Headers({ "Content-Type": "application/json" });
  const token = useAuthStore.getState().accessToken;
  if (token) headers.set("Authorization", `Bearer ${token}`);
  setCompanyHeader(headers);
  let response: Response;
  try {
    response = await fetch(API_BASE + path, { method: "POST", headers, body: JSON.stringify(body) });
  } catch {
    throw new ApiError({ title: "Нет связи с сервером", status: 0, detail: "Нет связи с сервером: проверьте сеть и повторите", code: "network" });
  }
  if (response.status === 401 && retry && (await refreshOnce().catch(() => false))) return requestBlob(path, body, false);
  if (!response.ok) {
    const text = await response.text();
    let data: Partial<Problem> | null = null;
    try {
      data = JSON.parse(text);
    } catch {
      /* not json */
    }
    throw new ApiError(problemOf(response, data));
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const encoded = /filename\*=UTF-8''([^;]+)/.exec(disposition);
  const plain = /filename="([^"]+)"/.exec(disposition);
  const filename = encoded ? decodeURIComponent(encoded[1]) : plain ? plain[1] : null;
  return { blob: await response.blob(), filename };
}

export const api = {
  blob: (path: string, body?: unknown) => requestBlob(path, body),
  get: <T>(path: string, params?: Record<string, unknown>) => request<T>(path + qs(params)),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

const PYDANTIC_RU: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/at most (\d+) character/, (m) => `не длиннее ${m[1]} символов`],
  [/at least (\d+) character/, (m) => `не короче ${m[1]} символов`],
  [/Field required/, () => "обязательное поле"],
  [/valid UUID/, () => "выберите значение из списка"],
  [/valid (date|datetime)/, () => "неверная дата"],
  [/valid (number|decimal|integer)|unable to parse string as a number/, () => "введите число"],
  [/greater than or equal to (\S+)/, (m) => `введите число не меньше ${m[1]}`],
  [/greater than (\S+)/, (m) => `введите число больше ${m[1]}`],
  [/^Value error, (.*)$/, (m) => m[1]],
  [/valid string/, () => "введите текст"],
  [/less than or equal to (\S+)/, (m) => `не больше ${m[1]}`],
  [/Input should be '/, () => "недопустимое значение"],
  [/valid boolean/, () => "да или нет"],
];

function translate(message: string): string {
  for (const [re, fn] of PYDANTIC_RU) {
    const m = message.match(re);
    if (m) return fn(m);
  }
  return message;
}

export interface FieldError {
  field: string;
  message: string;
  /** The server's own sentence about the field (it already names what and where): shown without «Поле «…»». */
  sentence?: boolean;
}

/** Common column names said in words when a form gives no label for them (never «Поле «location_id»»). */
const FIELD_WORDS: Record<string, string> = {
  location_id: "Ячейка",
  from_location_id: "Ячейка «откуда»",
  to_location_id: "Ячейка «куда»",
  warehouse_id: "Склад",
  storage_object_id: "Товар",
  batch_id: "Партия",
  state_id: "Состояние",
  zone_id: "Зона",
  quantity: "Количество",
  quantity_plan: "Количество по плану",
  quantity_fact: "Количество факт",
  line_id: "Строка документа",
  partner_id: "Контрагент",
  assignee_id: "Исполнитель",
  serials: "Серийные номера",
};

/** Field-level problems from a 422 (pydantic errors) or a 409 (unique value taken), ready for Form.setFields. */
export function fieldErrors(error: unknown): FieldError[] {
  if (!(error instanceof ApiError)) return [];
  const out: FieldError[] = [];
  if (Array.isArray(error.problem.errors)) {
    for (const e of error.problem.errors as { loc?: unknown[]; msg?: string }[]) {
      const loc = (e.loc ?? []).filter((part) => part !== "body" && part !== "query");
      const field = String(loc[0] ?? "");
      if (!field) continue;
      out.push({ field, message: translate(e.msg ?? "") });
    }
  }
  if (typeof error.problem.field === "string") {
    out.push({ field: error.problem.field, message: error.problem.detail ?? "значение уже используется", sentence: !!error.problem.detail });
  }
  return out;
}

/** «Что не так / где / как исправить» in one sentence; labels turn column names into field labels. */
export function describeError(error: unknown, labels?: Record<string, string>): string {
  const fields = fieldErrors(error);
  if (fields.length) {
    return fields
      .map((f) => (f.sentence ? f.message : `Поле «${labels?.[f.field] ?? FIELD_WORDS[f.field] ?? f.field}»: ${f.message}`))
      .join("; ");
  }
  return errorMessage(error);
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.problem.detail ?? error.problem.title;
  if (error instanceof Error) return error.message;
  return "Неизвестная ошибка";
}

/** IANA time zone of this device («Asia/Almaty»); empty when the browser does not say. */
export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    return "";
  }
}
